import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, combineLatest, of } from 'rxjs';
import { catchError, distinctUntilChanged, map, shareReplay, take } from 'rxjs/operators';

import { ApiService } from './api.service';
import { ProjectService } from './project.service';
import { Crypto } from '@services/helpers/encryption-utils';
import { FeatureFlagService } from '@services/helpers/feature-flag.service';
import { LocalStorageService } from '@services/helpers/local-storage.service';
import { ProjectService as ListingProjectService } from 'src/app/modules/my-listings/listing-projects/project.service';

/** localStorage key holding `{ [precinctId]: PersistedScope }`, encrypted like `active_sub_offices`. */
const PRECINCT_SCOPE_KEY = 'precinct_scope';

export const FEATURE_FLAG_CONSOLIDATED_PRECINCT_VIEW = 'consolidated_precinct_view';

/** Used by the banner when the section on screen has not named its records. */
export const DEFAULT_SECTION_LABEL = 'Records';

export type PrecinctViewMode = 'project' | 'precinct';

export interface Precinct {
  _id: string;
  name?: string;
  image?: string;
  office_id?: string;
  user_id?: string;
  selectedProjects?: string[];
  is_hidden?: boolean;
  /**
   * Legacy per-precinct opt-in. No longer gates anything — Precinct Leads is available to every
   * user — and is kept only so existing stored values deserialise.
   */
  is_consolidated_view_enabled?: boolean;
  /**
   * Resolved projects with server-computed access, added by the backend alongside the bare
   * `selectedProjects` ids. When present it is authoritative and saves a round trip; see
   * `loadAuthorisedProjects`.
   */
  projects?: PrecinctProject[];
}

export interface PrecinctProject extends ScopeProject {
  /** Computed server-side for the calling user, using the same rules as `isAccess()`. */
  has_access?: boolean;
}

export interface ScopeProject {
  _id: string;
  Project_Name: string;
}

interface PersistedScope {
  mode: PrecinctViewMode;
  projectIds: string[];
}

/**
 * The one source of truth for "which project(s) is the project page currently showing".
 *
 * A precinct groups several projects that stay entirely separate in the database. Every project
 * inside one gains a "Precinct Leads" toggle above the tab menu: off queries the current project,
 * on widens each section's query to every project in the precinct the user can reach. This service
 * owns that choice and hands every list the same answer.
 *
 * Consolidation is a read-time widening of the query and nothing more. Records are never reparented
 * — a lead returned under Precinct Leads still belongs to the project it was created in.
 *
 * Modelled on SettingService's sub-office block (setting.service.ts) — BehaviorSubject seeded from
 * encrypted localStorage, a distinctUntilChanged stream, and a `*Param()` helper the lists spread
 * into their request. The seeding matters for the same reason it does there: a section that boots
 * before this service has resolved the precinct still issues its first request with the right
 * filter instead of fetching once narrow and again a moment later.
 *
 * Deliberately conservative: in project mode `scopedProjectIdsParam()` returns null, not [], so the
 * `project_ids` key is omitted entirely and single-project requests stay byte-identical to what
 * they were before this feature existed. That is the whole backward-compatibility argument.
 */
@Injectable({
  providedIn: 'root',
})
export class PrecinctScopeService {
  constructor(
    private api: ApiService,
    private projectService: ProjectService,
    private listingProjectService: ListingProjectService,
    private featureFlags: FeatureFlagService,
    private localStorageService: LocalStorageService
  ) {}

  //#region State

  private currentProjectId: string | null = null;

  private activePrecinct = new BehaviorSubject<Precinct | null>(null);
  private authorisedProjects = new BehaviorSubject<ScopeProject[]>([]);
  private mode = new BehaviorSubject<PrecinctViewMode>('project');
  private selectedProjectIds = new BehaviorSubject<string[]>([]);

  /**
   * Whether the section currently on screen can consolidate at all.
   *
   * Opt-out: sections consolidate by default, because most of them are record lists and that is
   * the whole point. Not everything can, though. The price list has not been switched on yet.
   * Associations is access control for one project, not a record list — consolidating it would
   * mean editing B2's permissions from B1's page. The standalone calendar has no project filter to
   * widen. Those set this false while mounted, which hides the switch and forces their queries
   * back to the single project, rather than offering a control that quietly does nothing.
   *
   * A section that opts out must restore true on leaving, or the next section inherits the
   * suppression.
   */
  private sectionSupported = new BehaviorSubject<boolean>(true);

  /**
   * What the section on screen calls its records — "Leads", "EOIs", "Lots".
   *
   * The switch lives in each section's toolbar but the banner lives on the page shell, and both
   * have to name the same thing ("Precinct Leads" over a banner reading "…Precinct Leads"). The
   * noun is declared once by the toggle and read from here, so the two cannot drift apart.
   */
  private sectionLabel = new BehaviorSubject<string>(DEFAULT_SECTION_LABEL);

  /** Cached `GET /my-precint` for the session. Reset on logout or office change. */
  private precinctList$: Observable<Precinct[]> | null = null;
  private precinctListOfficeId: string | null = null;

  /** Name lookup for the Project column, covering every project the scope can produce. */
  private projectNames = new Map<string, string>();

  readonly activePrecinct$ = this.activePrecinct.asObservable();
  readonly authorisedProjects$ = this.authorisedProjects.asObservable();
  readonly mode$ = this.mode.asObservable().pipe(distinctUntilChanged());
  readonly selectedProjectIds$ = this.selectedProjectIds.asObservable();
  readonly sectionLabel$ = this.sectionLabel.asObservable().pipe(distinctUntilChanged());

  /**
   * Whether to render the selector at all. A precinct where the user can reach only one project
   * offers nothing to consolidate, so the control stays hidden rather than showing a dead toggle.
   */
  readonly available$ = combineLatest([this.activePrecinct$, this.authorisedProjects$, this.sectionSupported.asObservable()]).pipe(
    map(([precinct, projects, supported]) => supported && !!precinct && (projects?.length || 0) > 1),
    distinctUntilChanged()
  );

  /**
   * What every list actually sends. Never emits [] — an empty scope would silently widen most
   * endpoints to "no filter", which is the opposite of what an empty selection means here.
   */
  readonly scopedProjectIds$ = combineLatest([
    this.mode$,
    this.authorisedProjects$,
    this.selectedProjectIds$,
    this.sectionSupported.asObservable(),
  ]).pipe(
    map(() => this.getScopedProjectIds()),
    distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b))
  );

  //#endregion

  //#region Lifecycle

  /**
   * Resolve the precinct context for a project page. Called once by ProjectEditComponent from its
   * route-params subscription.
   *
   * A project record does not carry its precinct id — the only mapping is precinct.selectedProjects
   * — so this has to look the project up in the precinct list rather than reading it off the project.
   */
  resolveForProject(projectId: string): void {
    if (!projectId || projectId === this.currentProjectId) {
      return;
    }

    this.currentProjectId = projectId;
    this.activePrecinct.next(null);
    this.authorisedProjects.next([]);
    this.mode.next('project');
    this.selectedProjectIds.next([]);
    this.sectionSupported.next(true);

    if (!this.featureFlags.isFeatureEnabled(FEATURE_FLAG_CONSOLIDATED_PRECINCT_VIEW)) {
      return;
    }

    this.loadPrecinctList()
      .pipe(take(1))
      .subscribe(list => {
        // Stale guard: the user can navigate to another project while this is in flight.
        if (this.currentProjectId !== projectId) {
          return;
        }

        const matches = (list || []).filter(p => (p?.selectedProjects || []).includes(projectId));
        if (matches.length > 1) {
          console.warn(`[precinct-scope] Project ${projectId} belongs to ${matches.length} precincts; using "${matches[0]?.name}".`);
        }

        const precinct = matches[0];
        if (!precinct) {
          return;
        }

        // No per-precinct opt-in and no role gate: Precinct Leads is for every user who can reach
        // more than one project in the precinct. `available$` applies that one condition — a
        // precinct with a single reachable project still offers nothing to consolidate.
        this.loadAuthorisedProjects(precinct, projectId);
      });
  }

  /** Called from ProjectEditComponent.ngOnDestroy. Does NOT clear the persisted selection. */
  clearActiveProject(): void {
    this.currentProjectId = null;
    this.activePrecinct.next(null);
    this.authorisedProjects.next([]);
    this.mode.next('project');
    this.selectedProjectIds.next([]);
  }

  /** Full teardown, for logout. Drops the persisted selection and the precinct cache. */
  reset(): void {
    this.clearActiveProject();
    this.precinctList$ = null;
    this.precinctListOfficeId = null;
    this.projectNames.clear();
    this.localStorageService.RemoveItem(PRECINCT_SCOPE_KEY);
  }

  //#endregion

  //#region Resolution

  /** The office's precincts (`GET /my-precint`), cached for the session — also feeds Action Today's precinct picker. */
  loadPrecinctList(): Observable<Precinct[]> {
    const officeId = this.api.getOfficeId();
    if (!this.precinctList$ || this.precinctListOfficeId !== officeId) {
      this.precinctListOfficeId = officeId;
      this.precinctList$ = this.projectService.getPrecinct().pipe(
        map((response: any) => (response?.data || []) as Precinct[]),
        catchError(() => of([] as Precinct[])),
        shareReplay(1)
      );
    }
    return this.precinctList$;
  }

  /**
   * The precinct's projects, narrowed to the ones this user may reach.
   *
   * `GET /my-precint` now resolves this itself: each precinct carries `projects[]` with a
   * server-computed `has_access`, which is authoritative — the server owns project access, and
   * duplicating the association rules in the browser is how the two drift apart.
   *
   * The `getGridViewAllProject` path below is the fallback for a backend that predates that field.
   * It exists because `isAccess()` needs `projectassociation_settings`, which `/projects-name-list`
   * does not carry but the grid endpoint does (and it already accepts `precinct_id`).
   */
  private loadAuthorisedProjects(precinct: Precinct, projectId: string): void {
    if (Array.isArray(precinct.projects)) {
      this.applyAuthorisedProjects(
        precinct,
        projectId,
        precinct.projects.filter(p => p?._id && p.has_access !== false)
      );
      return;
    }

    this.projectService
      .getGridViewAllProject({
        precinct_id: precinct._id,
        userid: this.api.getUserId(),
        officeid: this.api.getOfficeId(),
      })
      .pipe(
        map((response: any) => (response?.data || []) as any[]),
        catchError(() => of([] as any[])),
        take(1)
      )
      .subscribe(projects => {
        const userTeams = this.getUserTeams();
        this.applyAuthorisedProjects(
          precinct,
          projectId,
          (projects || []).filter(p => p?._id && this.listingProjectService.isAccess(p, userTeams))
        );
      });
  }

  private applyAuthorisedProjects(precinct: Precinct, projectId: string, projects: any[]): void {
    // The user can navigate to another project while a request is in flight.
    if (this.currentProjectId !== projectId) {
      return;
    }

    const authorised: ScopeProject[] = (projects || [])
      .map(p => ({ _id: p._id, Project_Name: p.Project_Name || '' }))
      .sort((a, b) => (a.Project_Name || '').trimStart().localeCompare((b.Project_Name || '').trimStart()));

    // The current project must be in the list — the user is standing in it. If the response missed
    // it (paging, a stale association), add it back rather than producing a scope that excludes
    // the page you are on.
    if (!authorised.some(p => p._id === projectId)) {
      const fallbackName = this.projectNames.get(projectId) || '';
      authorised.unshift({ _id: projectId, Project_Name: fallbackName });
    }

    authorised.forEach(p => this.projectNames.set(p._id, p.Project_Name));

    this.activePrecinct.next(precinct);
    this.authorisedProjects.next(authorised);
    this.restorePersisted(precinct._id, authorised);
  }

  /** Team ids the user belongs to, as isAccess() expects them. */
  private getUserTeams(): any[] {
    const teams = Crypto.decrypt(this.localStorageService.GetItem('teams_id'));
    return Array.isArray(teams) ? teams : [];
  }

  //#endregion

  //#region Synchronous reads — for the first fetch a component makes, before it subscribes

  getMode(): PrecinctViewMode {
    return this.mode.value;
  }

  isPrecinctMode(): boolean {
    return this.mode.value === 'precinct' && !!this.activePrecinct.value && this.sectionSupported.value;
  }

  /**
   * Declare whether the section now on screen can consolidate. Call with `false` on entering a
   * section that cannot and `true` on leaving it, so the next section is not left suppressed.
   */
  setSectionSupport(supported: boolean): void {
    if (this.sectionSupported.value !== supported) {
      this.sectionSupported.next(supported);
    }
  }

  /**
   * Name the records this section shows, for the banner. Called by the toggle on entering and
   * cleared on leaving, so the banner never keeps naming the tab you just left.
   */
  setSectionLabel(label: string): void {
    const next = label || DEFAULT_SECTION_LABEL;
    if (this.sectionLabel.value !== next) {
      this.sectionLabel.next(next);
    }
  }

  getActivePrecinct(): Precinct | null {
    return this.activePrecinct.value;
  }

  getAuthorisedProjects(): ScopeProject[] {
    return this.authorisedProjects.value || [];
  }

  /** Always at least [currentProjectId]. Callers can use this without a null check. */
  getScopedProjectIds(): string[] {
    const current = this.currentProjectId ? [this.currentProjectId] : [];
    if (!this.isPrecinctMode()) {
      return current;
    }

    const authorised = this.getAuthorisedProjects().map(p => p._id);
    if (!authorised.length) {
      return current;
    }

    const selected = this.selectedProjectIds.value || [];
    if (!selected.length) {
      return authorised;
    }

    const intersection = authorised.filter(id => selected.includes(id));
    return intersection.length ? intersection : authorised;
  }

  /**
   * The `project_ids` request param, or null in project mode.
   *
   * Returns null rather than [] on purpose — callers spread it conditionally
   * (`...(scoped ? { project_ids: scoped } : {})`) so a single-project request carries no trace of
   * this feature and cannot trip new server-side behaviour.
   */
  scopedProjectIdsParam(): string | null {
    if (!this.isPrecinctMode()) {
      return null;
    }
    const ids = this.getScopedProjectIds();
    return ids?.length ? JSON.stringify(ids) : null;
  }

  /** Same contract as scopedProjectIdsParam, for endpoints taking a native array (POST bodies). */
  scopedProjectIdsArray(): string[] | null {
    if (!this.isPrecinctMode()) {
      return null;
    }
    const ids = this.getScopedProjectIds();
    return ids?.length ? ids : null;
  }

  /** Context only. The server must not expand this itself — the frontend owns the expansion. */
  precinctIdParam(): string | null {
    return this.isPrecinctMode() ? this.activePrecinct.value?._id || null : null;
  }

  /**
   * Spreadable scope params for a request: `{}` in the default view, `{ project_ids, precinct_id }`
   * in precinct view. Spread it into any params object — `...precinctScope.scopeParams()`.
   *
   * For the aggregate reporting endpoints the backend contract is stricter than for the list
   * endpoints: one query over `projectid IN (project_ids)`, never N per-project queries summed,
   * and every distinct/unique metric counted over the union. A buyer holding EOIs in two buildings
   * is one buyer at precinct level, not two. See docs/backend/precinct/precinct-consolidated-view.md §6.
   */
  scopeParams(): { project_ids?: string; precinct_id?: string } {
    const ids = this.scopedProjectIdsParam();
    if (!ids) {
      return {};
    }
    // Keys are only added when they have a value: ApiService.buildParams() sets every key it is
    // handed, so a `precinct_id: undefined` here would go out on the wire as the literal
    // "precinct_id=undefined".
    const precinctId = this.precinctIdParam();
    return precinctId ? { project_ids: ids, precinct_id: precinctId } : { project_ids: ids };
  }

  /**
   * Project name for a row that only carries an id. Falls back to '' rather than the raw id — a
   * hex string in a table cell is worse than a blank one.
   */
  projectNameById(id: string): string {
    if (!id) {
      return '';
    }
    return this.projectNames.get(id) || '';
  }

  /**
   * Reconcile against `meta.scoped_project_ids` — the ids the server actually honoured.
   *
   * The server re-validates every id and drops unauthorised ones silently rather than 403ing the
   * page, so a project the user lost access to since the precinct was resolved simply vanishes
   * from the results. Without this the chips would keep offering it and the counts would look
   * wrong for no visible reason.
   *
   * Only ever narrows: an id the server honoured but we did not ask for is ignored, since that
   * would mean the server widened the scope, which the contract forbids.
   */
  reconcileScopedProjectIds(meta: any): void {
    const honoured = meta?.scoped_project_ids;
    if (!this.isPrecinctMode() || !Array.isArray(honoured)) {
      return;
    }

    const authorised = this.getAuthorisedProjects();
    const dropped = this.getScopedProjectIds().filter(id => !honoured.includes(id));
    if (!dropped.length || dropped.length === this.getScopedProjectIds().length) {
      // Nothing dropped, or everything was — the latter means a transient failure rather than a
      // permission change, and emptying the picker over it would be worse than leaving it be.
      return;
    }

    const remaining = authorised.filter(p => !dropped.includes(p._id));
    if (!remaining.length) {
      return;
    }

    console.warn(`[precinct-scope] Server dropped ${dropped.length} project(s) from the scope; removing them from the picker.`);
    this.authorisedProjects.next(remaining);
    this.setProjectFilter((this.selectedProjectIds.value || []).filter(id => !dropped.includes(id)));
  }

  /** Seed the name map from any project list the app has already fetched. */
  registerProjectNames(projects: any[]): void {
    (projects || []).forEach(p => {
      const id = p?._id || p?.id;
      const name = p?.Project_Name || p?.name;
      if (id && name && !this.projectNames.has(id)) {
        this.projectNames.set(id, name);
      }
    });
  }

  //#endregion

  //#region Columns

  /**
   * Ensure a Project column exists when the list is showing more than one project.
   *
   * The backend appends this definition itself on scoped requests, so this is normally a no-op —
   * it covers the price list, which intersects response columns with the office's saved column set
   * (which will never contain `project`), and any endpoint that has not adopted the convention.
   *
   * Both key spellings are carried because the two list families in this codebase read different
   * ones, matching what the backend sends.
   */
  withProjectColumn(columns: any[]): any[] {
    if (!this.isPrecinctMode() || !Array.isArray(columns)) {
      return columns;
    }
    if (columns.some(col => col?.field === 'project')) {
      return columns;
    }
    return [{ type: 'text', field: 'project', column: 'Project', header: 'Project', label: 'Project', sortable: false }, ...columns];
  }

  //#endregion

  //#region Mutations

  setMode(mode: PrecinctViewMode): void {
    if (mode === 'precinct' && !this.activePrecinct.value) {
      return;
    }
    if (this.mode.value === mode) {
      return;
    }
    this.mode.next(mode);
    this.persist();
  }

  /** [] means "all authorised projects". */
  setProjectFilter(ids: string[]): void {
    const authorised = this.getAuthorisedProjects().map(p => p._id);
    const next = (ids || []).filter(id => authorised.includes(id));
    if (JSON.stringify(next) === JSON.stringify(this.selectedProjectIds.value)) {
      return;
    }
    this.selectedProjectIds.next(next);
    this.persist();
  }

  //#endregion

  //#region Persistence — keyed by precinct, never by project

  /**
   * Keyed by precinct so that turning on "Entire precinct" in B1 and then opening B2 keeps you in
   * precinct mode (same precinct, same data set). It also structurally prevents scope leaking:
   * a project in a different precinct reads a different key, and a project in no precinct reads
   * none, so both default to project mode.
   */
  private readPersisted(): Record<string, PersistedScope> {
    try {
      const raw = Crypto.decrypt(this.localStorageService.GetItem(PRECINCT_SCOPE_KEY));
      const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
      return {};
    }
  }

  private persist(): void {
    const precinctId = this.activePrecinct.value?._id;
    if (!precinctId) {
      return;
    }
    const all = this.readPersisted();
    all[precinctId] = { mode: this.mode.value, projectIds: this.selectedProjectIds.value || [] };
    this.localStorageService.SetItem(PRECINCT_SCOPE_KEY, Crypto.encrypt(JSON.stringify(all)));
  }

  private restorePersisted(precinctId: string, authorised: ScopeProject[]): void {
    const authorisedIds = authorised.map(p => p._id);
    const saved = this.readPersisted()[precinctId];

    // Default is every project selected, not an empty picker. Turning Precinct Leads on should
    // immediately show the whole precinct; making the user then choose the projects they already
    // asked for is a second decision to answer the same question.
    if (!saved) {
      this.selectedProjectIds.next(authorisedIds);
      return;
    }

    // Intersect with what the user can currently reach, so a project they lost access to since
    // the selection was saved drops out silently instead of producing an unauthorised request.
    const projectIds = (saved.projectIds || []).filter(id => authorisedIds.includes(id));

    this.selectedProjectIds.next(projectIds.length ? projectIds : authorisedIds);
    if (saved.mode === 'precinct' && authorised.length > 1) {
      this.mode.next('precinct');
    }
  }

  //#endregion

  //#region Cache invalidation

  /**
   * Drop the cached `GET /my-precint` response.
   *
   * The precinct list is fetched once per session, so a master admin flipping the consolidated-view
   * toggle would otherwise not see it take effect until a reload.
   */
  invalidatePrecinctCache(): void {
    this.precinctList$ = null;
  }

  //#endregion
}
